/**
 * ChoosePlanSection — shown at the top of the Settings → Subscription tab.
 *
 * Two tiles, side-by-side: Portfolio Clarity and Continuity System, with
 * the user's regional monthly + annual price. The current plan is marked.
 * The other plan has a single Switch button that opens UpgradeConfirmationModal.
 *
 * Data source: GET /api/pricing/v3 (returns both tier prices for the user's
 * country). Beta-free users see a small "included in your beta trial" line.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, TrendingUp, Shield, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import UpgradeConfirmationModal from './UpgradeConfirmationModal';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TIER_CONTINUITY = 'premium_continuity';

const fmt = (amount, symbol) => {
  if (amount == null) return '\u2014';
  const rounded = Number(amount);
  return symbol + rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export function ChoosePlanSection({ user, onTierChanged }) {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${BACKEND_URL}/api/pricing/v3`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setPricing(await r.json());
    } catch (err) {
      logger.error('load /pricing/v3', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 flex items-center justify-center" data-testid="choose-plan-loading">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!pricing) return null;

  const onContinuity = pricing.current_tier === TIER_CONTINUITY;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="choose-plan-section">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-lg font-serif font-semibold text-slate-900">Your plan</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlanTile
          testId="plan-tile-portfolio"
          icon={TrendingUp}
          accent="emerald"
          title="Portfolio Clarity"
          tagline="PORTFOLIO CLARITY"
          symbol={pricing.symbol}
          monthly={pricing.portfolio.monthly_amount}
          annual={pricing.portfolio.annual_amount}
          current={!onContinuity}
          betaExpiresAt={pricing.beta_expires_at}
        />
        <PlanTile
          testId="plan-tile-continuity"
          icon={Shield}
          accent="blue"
          title="Continuity System"
          tagline="WEALTH CONTINUITY"
          symbol={pricing.symbol}
          monthly={pricing.continuity.monthly_amount}
          annual={pricing.continuity.annual_amount}
          current={onContinuity}
          betaExpiresAt={pricing.beta_expires_at}
          switchButton={!onContinuity && (
            <Button
              size="sm"
              className="bg-blue-700 hover:bg-blue-800 text-white mt-3 w-full"
              data-testid="plan-tile-switch-continuity"
              onClick={() => setShowModal(true)}
            >
              Switch to Continuity System
            </Button>
          )}
        />
      </div>
      {onContinuity && (
        <p className="text-[11px] text-slate-500 mt-4">
          To move back to Portfolio Clarity, please contact
          {' '}<a className="underline" href="mailto:support@myinvestfolio.io">support@myinvestfolio.io</a>.
          Your Continuity System contents remain safe during the switch.
        </p>
      )}
      {showModal && (
        <UpgradeConfirmationModal
          pricing={pricing}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); load(); if (onTierChanged) onTierChanged(); }}
        />
      )}
    </div>
  );
}

const accentClasses = (accent) => (accent === 'blue'
  ? { border: 'border-blue-200', bg: 'bg-blue-50/50', text: 'text-blue-800', tag: 'text-blue-700' }
  : { border: 'border-emerald-200', bg: 'bg-emerald-50/50', text: 'text-emerald-800', tag: 'text-emerald-700' });

const PlanTile = ({ testId, icon: Icon, accent, title, tagline, symbol, monthly, annual, current, betaExpiresAt, switchButton }) => {
  const c = accentClasses(accent);
  return (
    <div className={`border rounded-lg p-4 ${c.border} ${c.bg}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-5 h-5 ${c.tag}`} aria-hidden="true" />
        <h4 className="text-base font-semibold text-slate-900">{title}</h4>
        {current && (
          <span className={`ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${c.text}`} data-testid={`${testId}-current-badge`}>
            <Check className="w-3 h-3" /> Current
          </span>
        )}
      </div>
      <p className={`text-[11px] uppercase tracking-wide font-medium mb-3 ${c.tag}`}>{tagline}</p>
      <div className="text-2xl font-semibold text-slate-900">
        {fmt(monthly, symbol)}<span className="text-sm font-normal text-slate-500">/month</span>
      </div>
      <div className="text-xs text-slate-500">or {fmt(annual, symbol)}/year <span className="text-emerald-700 font-medium">(2 months free)</span></div>
      {betaExpiresAt && (
        <div className="text-[11px] text-slate-500 mt-2 italic" data-testid={`${testId}-beta-note`}>
          Included in your beta trial until {String(betaExpiresAt).slice(0, 10)}.
        </div>
      )}
      {switchButton}
    </div>
  );
};

export default ChoosePlanSection;
